import {
  Business as BusinessIcon,
  CheckCircle as CheckIcon,
  AccessTime as ClockIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Engineering as InProgressIcon,
  MoreVert as MoreVertIcon,
  Phone as PhoneIcon,
  Print as PrintIcon,
  Flag as PriorityIcon,
  QrCode as QrCodeIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  LocalShipping as TruckIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { usePermission } from "../../components/common/PermissionGuard";
import { formatDate, formatStatusText,capitalizeWords } from "../../utils/formatters";
import { Order } from "./order";

interface DetailedOrderListProps {
  orders: Order[];
  loading?: boolean;
  onEdit?: (order: Order) => void;
  onView?: (orderId: string) => void;
  onPrint?: (order: Order) => void;
  onRefresh?: () => void;
  onExport?: () => void;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  filterStatus?: string;
  onStatusFilterChange?: (status: string) => void;
  filterPriority?: string;
  onPriorityFilterChange?: (priority: string) => void;
  sortBy?: string;
  onSortChange?: (sortBy: string) => void;
  showFilters?: boolean;
  showActions?: boolean;
  selectedOrderId?: string | null;
}

const DetailedOrderList: React.FC<DetailedOrderListProps> = ({
  orders = [],
  loading = false,
  onEdit,
  onView,
  onPrint,
  onRefresh,
  onExport,
  searchTerm = "",
  onSearchChange,
  filterStatus = "all",
  onStatusFilterChange,
  filterPriority = "all",
  onPriorityFilterChange,
  sortBy = "dueDate",
  onSortChange,
  showFilters = true,
  showActions = true,
  selectedOrderId = null,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const selectedOrderRef = useRef<string | null>(null);

  // Scroll to selected order when it changes
  useEffect(() => {
    if (selectedOrderId && selectedOrderId !== selectedOrderRef.current) {
      selectedOrderRef.current = selectedOrderId;
      setTimeout(() => {
        const element = document.getElementById(`order-${selectedOrderId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [selectedOrderId]);

  const canEdit = usePermission("orders", "update");
  const canView = usePermission("orders", "read");

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <ClockIcon fontSize="small" sx={{ color: "warning.main" }} />;
      case "in_progress":
        return <InProgressIcon fontSize="small" sx={{ color: "info.main" }} />;
      case "completed":
        return <CheckIcon fontSize="small" sx={{ color: "success.main" }} />;
      case "dispatched":
        return <TruckIcon fontSize="small" sx={{ color: "secondary.main" }} />;
      default:
        return <ClockIcon fontSize="small" sx={{ color: "text.secondary" }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning";
      case "in_progress":
        return "info";
      case "completed":
        return "success";
      case "dispatched":
        return "secondary";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const getPriorityIcon = (priority: string) => {
    const colors = {
      urgent: theme.palette.error.main,
      high: theme.palette.warning.main,
      medium: theme.palette.info.main,
      low: theme.palette.success.main,
    };
    return (
      <PriorityIcon
        fontSize="small"
        sx={{
          color:
            colors[priority as keyof typeof colors] ||
            theme.palette.text.secondary,
        }}
      />
    );
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    order: Order
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrder(null);
  };

  const handleAction = (action: string) => {
    if (!selectedOrder) return;

    switch (action) {
      case "view":
        onView?.(selectedOrder.id);
        break;
      case "edit":
        onEdit?.(selectedOrder);
        break;
      case "print":
        onPrint?.(selectedOrder);
        break;
      case "qr":
        // Handle QR code action
        break;
      default:
        break;
    }
    handleMenuClose();
  };

  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const isOverdue = (dueDate: string) => {
    if (!dueDate) return false;
    const date = new Date(dueDate);
    if (isNaN(date.getTime())) return false;
    return date < new Date();
  };

  const getOrderSummary = (order: Order) => {
    const totalItems = order.totalItems || 0;
    const completedItems = order.completedItems || 0;
    const progress =
      totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

    return {
      totalItems,
      completedItems,
      progress,
      remainingItems: totalItems - completedItems,
    };
  };

  const sortedOrders = useMemo(() => {
    if (!orders.length) return [];

    return [...orders].sort((a, b) => {
      switch (sortBy) {
        case "createdAt":
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          // Handle invalid dates by putting them at the end
          if (isNaN(dateA) && isNaN(dateB)) return 0;
          if (isNaN(dateA)) return 1;
          if (isNaN(dateB)) return -1;
          return dateB - dateA;
        case "dueDate":
          const dueDateA = new Date(a.dueDate).getTime();
          const dueDateB = new Date(b.dueDate).getTime();
          // Handle invalid dates by putting them at the end
          if (isNaN(dueDateA) && isNaN(dueDateB)) return 0;
          if (isNaN(dueDateA)) return 1;
          if (isNaN(dueDateB)) return -1;
          return dueDateA - dueDateB;
        case "priority":
          const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
          return (
            (priorityOrder[b.priority as keyof typeof priorityOrder] || 0) -
            (priorityOrder[a.priority as keyof typeof priorityOrder] || 0)
          );
        case "status":
          const statusOrder = {
            pending: 1,
            in_progress: 2,
            completed: 3,
            dispatched: 4,
          };
          return (
            (statusOrder[a.status as keyof typeof statusOrder] || 0) -
            (statusOrder[b.status as keyof typeof statusOrder] || 0)
          );
        case "customer":
          return a.customer?.name?.localeCompare(b.customer?.name || "") || 0;
        case "amount":
          return (b.totalAmount as number) - (a.totalAmount as number);
        default:
          return 0;
      }
    });
  }, [orders, sortBy]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Site Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "grey.300",
                          borderRadius: "50%",
                          mr: 2,
                        }}
                      />
                      <Box>
                        <Box
                          sx={{
                            height: 20,
                            bgcolor: "grey.300",
                            borderRadius: 1,
                            mb: 1,
                            width: 120,
                          }}
                        />
                        <Box
                          sx={{
                            height: 16,
                            bgcolor: "grey.300",
                            borderRadius: 1,
                            width: 80,
                          }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 16,
                        bgcolor: "grey.300",
                        borderRadius: 1,
                        width: 100,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 16,
                        bgcolor: "grey.300",
                        borderRadius: 1,
                        width: 80,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 24,
                        bgcolor: "grey.300",
                        borderRadius: 1,
                        width: 80,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 24,
                        bgcolor: "grey.300",
                        borderRadius: 1,
                        width: 60,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 8,
                        bgcolor: "grey.300",
                        borderRadius: 1,
                        width: 100,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 16,
                        bgcolor: "grey.300",
                        borderRadius: 1,
                        width: 80,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 16,
                        bgcolor: "grey.300",
                        borderRadius: 1,
                        width: 60,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 32,
                        bgcolor: "grey.300",
                        borderRadius: 1,
                        width: 40,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  const handleResetFilters = () => {
    onSearchChange?.("");
    onStatusFilterChange?.("all");
    onPriorityFilterChange?.("all");
    onSortChange?.("dueDate");
  }

  return (
    <Box sx={{ p: 0, pb: 2 }}>
      {/* Filters and Search */}
      {showFilters && (
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => onSearchChange?.(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      sx={{
                        color: "text.secondary",
                        fontSize: 20,
                        mr: 1,
                      }}
                    />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => onStatusFilterChange?.(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="dispatched">Dispatched</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filterPriority}
                  onChange={(e) => onPriorityFilterChange?.(e.target.value)}
                  label="Priority"
                >
                  <MenuItem value="all">All Priority</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => onSortChange?.(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="createdAt">Created Date</MenuItem>
                  <MenuItem value="dueDate">Due Date</MenuItem>
                  <MenuItem value="priority">Priority</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="amount">Amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterAltOffOutlinedIcon />}
                onClick={handleResetFilters}
                size="small"
              >
                Reset
              </Button>
            </Grid>

            <Grid item xs={6} md={2}>
              {/* <Stack direction="row" spacing={1}>
                <Tooltip title="Refresh">
                  <IconButton onClick={onRefresh} size="small">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                
              </Stack> */}
               <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={onRefresh}
                  size="small"
                >
                  Refresh
                </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Detailed Orders Table */}
      <TableContainer
        component={Paper}
        sx={{ borderRadius: 2, overflowX: "auto", overflowY: "hidden" }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Site</TableCell>
              {/* <TableCell sx={{ fontWeight: 600 }}>Status & Priority</TableCell> */}
              <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Total Sets</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Total Parts</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedOrders.map((order) => {
              const summary = getOrderSummary(order);
              const isExpanded = expandedOrders.has(order.id);
              const overdue = isOverdue(order.dueDate);

              const isSelected = selectedOrderId === order.id;
              
              return (
                <React.Fragment key={order.id}>
                  <TableRow
                    id={`order-${order.id}`}
                    hover
                    sx={{
                      cursor: "pointer",
                      bgcolor: isSelected 
                        ? alpha(theme.palette.primary.main, 0.1) 
                        : "transparent",
                      borderLeft: isSelected 
                        ? `4px solid ${theme.palette.primary.main}` 
                        : "4px solid transparent",
                      "&:hover": {
                        bgcolor: isSelected 
                          ? alpha(theme.palette.primary.main, 0.15) 
                          : alpha(theme.palette.primary.main, 0.04),
                      },
                      transition: "background-color 0.2s ease, border-left 0.2s ease",
                    }}
                    onClick={() => onView?.(order.id)}
                  >
                    {/* Order Details */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              lineHeight: 1.2,
                              mb: 0.5,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {order.orderNumberNew}
                          </Typography>

                          {/* <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {order.productStatistics?.completedProducts}/{order.productStatistics?.totalProducts} products • {order.itemStatistics?.completedItems || summary.completedItems}/{order.itemStatistics?.totalItems || summary.totalItems} items
                          </Typography> */}
                          {/* {overdue && (
                            <Chip
                              label="Overdue"
                              size="small"
                              color="error"
                              variant="filled"
                              sx={{ mt: 0.5, fontSize: "0.7rem", height: 20 }}
                            />
                          )} */}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Created Date */}
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {order.createdAt ? formatDate(order.createdAt, "numeric") : '-'}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Customer */}
                    <TableCell>
                      <Box>
                        <Typography
                          title={order.customer?.name }
                          variant="body2"
                          // sx={{ fontWeight: 500, mb: 0.5 }}
                          noWrap
                          sx={{ fontWeight: 500, mb: 0.5, maxWidth: 150 }}
                        >
                          { capitalizeWords(order.customer?.name)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.customer?.contactPerson}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mt: 0.5,
                          }}
                        >
                          <PhoneIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: "text.secondary" }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {order.customer?.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Site Name */}
                    <TableCell>
                      <Tooltip 
                        title={
                          <Box>
                            <strong>{order?.site?.name}</strong>
                            <br />
                            {order?.site?.address}, {order?.site?.city}
                          </Box>
                        }
                        arrow
                      >
                        <Box>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 500, maxWidth: "200px", display: "block" }}>
                            {capitalizeWords(order.site?.name) ||
                              order.site_tracking_code ||
                              "N/A"}
                          </Typography>                         
                      
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            noWrap
                            sx={{ 
                              maxWidth: "200px",
                              display: "block"
                            }}
                          >
                            {order.site?.address},{order.site?.city}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {/* Status & Priority */}
                    {/* <TableCell>
                      <Stack spacing={1}>
                        <Chip
                          icon={getStatusIcon(order.status)}
                          label={formatStatusText(order.status)}
                          size="small"
                          color={getStatusColor(order.status) as any}
                          variant="outlined"
                        />
                        <Chip
                          icon={getPriorityIcon(order.priority)}
                          label={order.priority}
                          size="small"
                          color={getPriorityColor(order.priority) as any}
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell> */}

                    {/* Due Date */}
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {order.dueDate ? formatDate(order.dueDate, "numeric") : 'Not Set'}
                        </Typography>
                        {/* <Typography variant="caption" color="text.secondary">
                          {overdue ? "Overdue" : "On time"}
                        </Typography> */}
                      </Box>
                    </TableCell>

                    {/* Amount */}
                    <TableCell>
                      {/* <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ₹
                        {typeof order.totalAmount === "string"
                          ? order.totalAmount
                          : order.totalAmount?.toLocaleString() || "0"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {order.paymentStatus}
                      </Typography> */}
                      <Typography variant="body2" >
                        {order.paymentStatus?.charAt(0).toUpperCase() +
                          order.paymentStatus?.slice(1)}
                      </Typography>
                    </TableCell>

                    {/* Products */}
                    <TableCell>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          // color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                          }}
                        >
                          {order.productStatistics?.completedProducts}/
                          {order.productStatistics?.totalProducts}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Items */}
                    <TableCell>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          // color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                          }}
                        >
                          {order.itemStatistics?.completedItems ||
                            summary.completedItems}
                          /
                          {order.itemStatistics?.totalItems ||
                            summary.totalItems}{" "}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Enhanced Progress with Comprehensive Statistics */}
                    <TableCell>
                      <Box sx={{ minWidth: 30 }}>
                        {/* Overall Progress */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="caption" fontWeight="medium">
                            {order.orderProgress?.overallProgressPercentage ||
                              summary.progress}
                            %
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={
                            order.orderProgress?.overallProgressPercentage ||
                            summary.progress
                          }
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 3,
                            },
                          }}
                        />

                        {/* Legacy fallback */}
                        {!order.productStatistics && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, display: "block" }}
                          >
                            {summary.completedItems}/{summary.totalItems}{" "}
                            completed
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* Status */}
                    <TableCell
                      sx={{
                        verticalAlign: "middle",
                      }}
                    >
                      <Stack spacing={1}>
                        {/* <Chip
                          icon={getStatusIcon(order.status)}
                          // label={order.status.replace("_", " ")}
                          size="small"
                          color={getStatusColor(order.status) as any}
                          variant="outlined"
                        /> */}
                        {getStatusIcon(order.status)}
                        {/* <Chip
                          icon={getPriorityIcon(order.priority)}
                          label={order.priority}
                          size="small"
                          color={getPriorityColor(order.priority) as any}
                          variant="outlined"
                        /> */}
                      </Stack>
                    </TableCell>

                    {/* Priority */}
                    <TableCell>
                      <Stack spacing={1}>
                        <Chip
                          // icon={getPriorityIcon(order.priority)}
                          // label={order.priority}
                          label={order.priority.toUpperCase()}
                          size="small"
                          color={getPriorityColor(order.priority) as any}
                          variant="outlined"
                        />
                        {/* {getPriorityIcon(order.priority)} */}
                      </Stack>
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        {canView && (
                          <>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onView?.(order.id);
                                }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Print QR Codes">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onPrint?.(order);
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {/* {canEdit && (
                          <Tooltip title="Edit Order">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit?.(order);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )} */}
                        {/* <Tooltip title="More Actions">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, order)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip> */}
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row - Product Details */}
                  {isExpanded && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          p: 0,
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                              Products & Items
                            </Typography>
                            <Grid container spacing={2}>
                              {order.products?.map((product) => (
                                <Grid item xs={12} md={6} key={product.id}>
                                  <Card variant="outlined">
                                    <CardContent>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "start",
                                          mb: 2,
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle1"
                                          sx={{ fontWeight: 600 }}
                                        >
                                          {product.name || product.type}
                                        </Typography>
                                        <Chip
                                          label={product.type}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                        />
                                      </Box>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Items: {product.total_items}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Completed: {product.completed_items}
                                        </Typography>
                                      </Box>
                                      <LinearProgress
                                        variant="determinate"
                                        value={
                                          product.total_items > 0
                                            ? (product.completed_items /
                                                product.total_items) *
                                              100
                                            : 0
                                        }
                                        sx={{ mb: 1 }}
                                      />
                                      {product.items &&
                                        product.items.length > 0 && (
                                          <Box sx={{ mt: 1 }}>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              gutterBottom
                                            >
                                              Items:
                                            </Typography>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 0.5,
                                                mt: 0.5,
                                              }}
                                            >
                                              {product.items
                                                .slice(0, 5)
                                                .map((item) => (
                                                  <Chip
                                                    key={item.id}
                                                    label={`${
                                                      item.type
                                                        ?.charAt(0)
                                                        .toUpperCase() +
                                                      item.type?.slice(1)
                                                    } ${item.id.slice(-4)}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color={
                                                      getStatusColor(
                                                        item.status
                                                      ) as any
                                                    }
                                                  />
                                                ))}
                                              {product.items.length > 5 && (
                                                <Chip
                                                  label={`+${
                                                    product.items.length - 5
                                                  } more`}
                                                  size="small"
                                                  variant="outlined"
                                                  color="default"
                                                />
                                              )}
                                            </Box>
                                          </Box>
                                        )}
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Empty State */}
      {sortedOrders.length === 0 && !loading && (
        <Paper
          sx={{
            p: 6,
            textAlign: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            borderRadius: 2,
          }}
        >
          <BusinessIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No orders found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || filterStatus !== "all" || filterPriority !== "all"
              ? "Try adjusting your filters or search terms"
              : "Create your first order to get started"}
          </Typography>
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleAction("view")}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {canEdit && (
          <MenuItem onClick={() => handleAction("edit")}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Order</ListItemText>
          </MenuItem>
        )}
        {/* <MenuItem onClick={() => handleAction("print")}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print QR Codes</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction("qr")}>
          <ListItemIcon>
            <QrCodeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View QR Codes</ListItemText>
        </MenuItem> */}
      </Menu>
    </Box>
  );
};

export default DetailedOrderList;
